import React, { useEffect, useMemo } from 'react'
import cx from 'clsx'

import Select, { useSelectHelpers } from '@components/Form/ReactSelect'

import { useGetMyAccount } from '@queries/Account'
import { useGetCryptoCurrencies } from '@queries/CryptoCurrencies'
import { useGetApproveitOrganization } from '@queries/Accounting'
import { currencyOptions, priorityCurrencies } from '@src/constants'
import { useStore } from '@src/store'

import '@tabler/core/dist/css/tabler-flags.min.css'
import type { ActionMeta, OnChangeValue } from 'react-select'

export interface CurrencyOption extends Option<string> {
  flag?: string
}

interface CurrencySelectorProps {
  onChange: (newValue: OnChangeValue<Option, false>, actionMeta: ActionMeta<Option>) => void
  isCrypto?: boolean
  value?: string
  name?: string
  label?: string
  isClearable?: boolean
  isDisabled?: boolean
  menuPortalTarget?: HTMLElement | null
  required?: boolean
  placeholder?: string
  className?: string
  hideDefaultLabel?: boolean
  errorMessage?: string
  classes?: {
    control?: string
    label?: string
    container?: string
    inputContainer?: string
    errorMessage?: string
  }
  optionsFilter?: (option: CurrencyOption) => boolean
}

const CurrencySelector: React.FC<CurrencySelectorProps> = ({
  onChange,
  name = 'currency',
  label,
  isClearable = false,
  isDisabled = false,
  menuPortalTarget = null,
  required = false,
  placeholder = 'Search currency...',
  className,
  value,
  hideDefaultLabel = false,
  errorMessage,
  classes,
  optionsFilter,
  isCrypto = false,
}) => {
  const { data: user } = useGetMyAccount()
  const { data: cryptoCurrencies } = useGetCryptoCurrencies(isCrypto)
  const org = useStore.use.organization()
  const { data: organization } = useGetApproveitOrganization(org)

  const currencyNames = new Intl.DisplayNames(['en'], { type: 'currency' })
  const defaultCurrency = user?.team?.default_currency
  const organizationCurrency = organization?.currency

  const getCurrencies = () => {
    if (isCrypto) {
      return cryptoCurrencies!.map(({ symbol }) => ({
        code: symbol,
        name: symbol,
        flag: undefined,
      }))
    } else {
      return currencyOptions.map((currency) => ({
        code: currency.value,
        name: currencyNames.of(currency.value),
        flag: currency.value.slice(0, 2).toLowerCase(),
      }))
    }
  }

  useEffect(() => {
    if (!value && required && !isCrypto && organization) {
      const currency = currencyOptions.find((currency) => currency.value === (organizationCurrency || defaultCurrency))
      if (currency) {
        onChange(currency, {
          action: 'select-option',
          name,
          option: currency,
        })
      }
    }
  }, [value, organization])

  const currencies = getCurrencies()

  const formatOptionLabel = (option: CurrencyOption) => {
    const isOrganizationCurrency = option.value === organizationCurrency
    const isDefaultCurrency = option.value === defaultCurrency

    if (isCrypto) {
      return (
        <div className="flex items-center gap-3 w-full">
          <div className="flex gap-1 items-center truncate justify-between w-full">
            <span>{option.label}</span>
          </div>
        </div>
      )
    } else {
      return (
        <div className="flex items-center gap-3 w-full">
          <div className="relative min-w-[22px] h-3.5">
            <div
              className={cx(
                `!absolute !bg-[length:22px_14px] top-0 left-0 !w-[22px] !h-3.5 flag flag-xs flag-country-${option.flag}`,
                option.value === 'EUR' && '!bg-[length:23px_15px] !bg-[0_-1px]',
              )}
            />
            <div className="absolute top-0 left-0 w-full -z-[1] h-full border bg-neutral-50 rounded-sm" />
          </div>
          <div className="flex gap-1 items-center truncate justify-between w-full">
            <div className="flex gap-1 items-center truncate">
              <span>{`${option.value}${' '}`}</span>
              <span className="text-neutral-500 truncate">{`• ${option.label}`}</span>
            </div>
            {!hideDefaultLabel && (isDefaultCurrency || isOrganizationCurrency) && (
              <span className="inline-block truncate leading-[10px] text-xs ml-2 bg-green-200 text-green-800 px-2 py-1 rounded-base">
                {isOrganizationCurrency ? 'Organization currency' : 'Team currency'}
              </span>
            )}
          </div>
        </div>
      )
    }
  }

  const sortOptions = () => {
    if (isCrypto) {
      let options = currencies.map((currency) => ({
        value: currency.code,
        label: currency.name,
      })) as CurrencyOption[]

      if (optionsFilter) {
        options = options.filter(optionsFilter)
      }

      return options.sort((a, b) => a.label.localeCompare(b.label))
    } else {
      const defaultCurrencyObj = currencies.find((currency) => currency.code === defaultCurrency)
      const filteredPriorityCurrencies = priorityCurrencies.filter((code) => code !== defaultCurrency)
      const priorityCurrenciesList = filteredPriorityCurrencies
        .map((code) => currencies.find((currency) => currency.code === code))
        .filter((currency) => currency)

      const otherCurrencies = currencies.filter(
        (currency) => !priorityCurrencies.includes(currency.code) && currency.code !== defaultCurrency,
      )

      const sortedOtherCurrencies = otherCurrencies.sort((a, b) => {
        if (a.name && b.name) {
          return a.name.localeCompare(b.name)
        }
        return 0
      })
      let options = [defaultCurrencyObj, ...priorityCurrenciesList, ...sortedOtherCurrencies]
        .filter(Boolean)
        .map((currency) => {
          if (!currency) return null
          return {
            value: currency.code,
            label: currency.name,
            flag: currency.flag,
          }
        }) as CurrencyOption[]

      if (optionsFilter) {
        options = options.filter(optionsFilter)
      }
      return options
    }
  }

  const formattedOptions = useMemo(() => sortOptions(), [organization])

  const { optionValue } = useSelectHelpers(formattedOptions as Option<string>[], value)
  return (
    <Select
      className={cx(className)}
      options={formattedOptions as CurrencyOption[]}
      formatOptionLabel={formatOptionLabel}
      name={name}
      onChange={onChange}
      value={optionValue}
      isDisabled={isDisabled}
      isClearable={isClearable}
      menuPortalTarget={menuPortalTarget}
      required={required}
      placeholder={placeholder}
      label={label}
      errorMessage={errorMessage}
      classes={{
        label: cx(classes?.label),
        control: cx(classes?.control),
        container: cx(classes?.container),
        inputContainer: cx(classes?.inputContainer),
        errorMessage: cx(classes?.errorMessage),
      }}
    />
  )
}

export default CurrencySelector
